import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import relativeTime from 'dayjs/plugin/relativeTime';

import { useAccountLegalEntity, useAccountSettings } from '@/entities/account';
import {
  ChangeEmailDialog,
  ChangeLanguageDialog,
  ChangePasswordDialog,
  ChangePhoneDialog,
  ChangeTimezoneDialog,
  LegalEntityDialog,
  TwoFaSwitcher,
} from '@/features/accountSettings';
import { LogoutButton } from '@/features/auth';
import { usePageTitle } from '@/features/pageTitle';
import { Divider } from '@/shared/ui';
import { Button } from '@/shared/ui/lib/Button';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { PageContainer } from '@/shared/ui/PageContainer';
import { SvgIcon } from '@/shared/ui/SvgIcon';
import { dayjs } from '@/shared/utils/dayjs';

import { styles } from './styles/setting-item-styles';
import { StyledSettingContainer } from './styles/settings-container-styles';
import { SettingItem } from './SettingItem';

dayjs.extend(relativeTime);

export const AccountSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { accountSettings } = useAccountSettings();
  const { legalEntity } = useAccountLegalEntity();

  usePageTitle(t('common:menu.accountSettings'));

  const hasValidPasswordDate = (date?: string) =>
    date && !date.startsWith('0001-01-01');

  const [openChangeEmailDialog, setOpenChangeEmailDialog] = useState(false);
  const [openChangeLanguageDialog, setOpenChangeLanguageDialog] =
    useState(false);
  const [openChangeTimezoneDialog, setOpenChangeTimezoneDialog] =
    useState(false);
  const [openChangePasswordDialog, setOpenChangePasswordDialog] =
    useState(false);
  const [openChangePhoneDialog, setOpenChangePhoneDialog] = useState(false);
  const [openLegalEntityDialog, setOpenLegalEntityDialog] = useState(false);

  const legalEntityDescription =
    legalEntity?.entity_type && legalEntity.name
      ? [
          t(`settings:legalEntity.entityTypes.${legalEntity.entity_type}`),
          legalEntity.name,
          `${t('settings:legalEntity.fields.inn')} ${legalEntity.inn}`,
          legalEntity.kpp
            ? `${t('settings:legalEntity.fields.kpp')} ${legalEntity.kpp}`
            : undefined,
        ]
          .filter(Boolean)
          .join(' · ')
      : t('settings:legalEntity.empty');

  return (
    <PageContainer>
      {accountSettings ? (
        <>
          <StyledSettingContainer>
            <SettingItem
              title={t('settings:account.email.title')}
              description={
                accountSettings?.email ?? t('settings:account.email.noData')
              }
              actionSlot={
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setOpenChangeEmailDialog(true)}
                >
                  {t('common:buttons.edit')}
                </Button>
              }
            />
            <Divider sx={styles.divider} />
            <SettingItem
              title={t('settings:account.password.title')}
              description={
                hasValidPasswordDate(accountSettings?.password_changed_at)
                  ? `${t('settings:account.password.lastChanged')} ${dayjs(accountSettings.password_changed_at).subtract(1, 'second').fromNow()}`
                  : t('settings:account.password.noData')
              }
              actionSlot={
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setOpenChangePasswordDialog(true)}
                >
                  {t('common:buttons.edit')}
                </Button>
              }
            />
            <Divider sx={styles.divider} />
            <SettingItem
              title={t('settings:account.language.title')}
              description={t(`common:lang.${i18n.language.split('-')[0]}.long`)}
              actionSlot={
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setOpenChangeLanguageDialog(true)}
                >
                  {t('common:buttons.edit')}
                </Button>
              }
            />
            <Divider sx={styles.divider} />
            <SettingItem
              title={t('settings:account.phone.title')}
              description={accountSettings?.phone}
              actionSlot={
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setOpenChangePhoneDialog(true)}
                >
                  {t('common:buttons.edit')}
                </Button>
              }
            />
            <Divider sx={styles.divider} />
            <SettingItem
              title={t('settings:account.timezone.title')}
              description={t(`common:timezone.${accountSettings?.timezone}`)}
              actionSlot={
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setOpenChangeTimezoneDialog(true)}
                >
                  {t('common:buttons.edit')}
                </Button>
              }
            />
            <Divider sx={styles.divider} />
            <SettingItem
              title={t('settings:legalEntity.title')}
              description={legalEntityDescription}
              actionSlot={
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setOpenLegalEntityDialog(true)}
                >
                  {t('common:buttons.edit')}
                </Button>
              }
            />
            <Divider sx={styles.divider} />
            <SettingItem
              title={t('settings:account.2fa.title')}
              description={t('settings:account.2fa.description')}
              actionSlot={<TwoFaSwitcher />}
            />
            {/* <Divider sx={styles.divider} />
            <SettingItem
              title={t('settings:account.blockedContacts.title')}
              description={t('settings:account.blockedContacts.empty')}
            />
            <Divider sx={styles.divider} />
            <SettingItem
              title={t('settings:account.groupChats.title')}
              description={t('settings:account.groupChats.description')}
              actionSlot={
                <Button variant="text" size="small">
                  {t('common:buttons.enable')}
                </Button>
              }
            /> */}
          </StyledSettingContainer>

          <ChangeEmailDialog
            open={openChangeEmailDialog}
            onClose={() => setOpenChangeEmailDialog(false)}
          />

          <ChangeLanguageDialog
            open={openChangeLanguageDialog}
            onClose={() => setOpenChangeLanguageDialog(false)}
          />

          <ChangeTimezoneDialog
            open={openChangeTimezoneDialog}
            onClose={() => setOpenChangeTimezoneDialog(false)}
          />

          <ChangePhoneDialog
            open={openChangePhoneDialog}
            onClose={() => setOpenChangePhoneDialog(false)}
          />

          <ChangePasswordDialog
            open={openChangePasswordDialog}
            onClose={() => setOpenChangePasswordDialog(false)}
          />

          <LegalEntityDialog
            open={openLegalEntityDialog}
            onClose={() => setOpenLegalEntityDialog(false)}
            legalEntity={legalEntity}
          />
        </>
      ) : (
        <NotFoundData
          title={t('settings:errors.loadError')}
          sx={{ marginBottom: '20px' }}
        />
      )}

      <LogoutButton
        variant="text"
        endIcon={<SvgIcon name="logout" />}
        sx={{ color: (theme) => theme.palette.custom.red }}
      />
    </PageContainer>
  );
};
